// src/estimatelist.js
import React, { useEffect, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const viewedTitle = (value) => {
  const viewedAt = new Date(value);
  if (Number.isNaN(viewedAt.getTime())) return 'Public link viewed';
  return `Public link first viewed: ${viewedAt.toLocaleString()}`;
};

const EstimateList = ({
  estimates,
  onEdit,
  onDuplicate,
  onDelete,
  onViewPrint,
  onSetStatus,
  onConvertToInvoice,
  listControls,
  isGlobalSearchActive
}) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [hoverKey, setHoverKey] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [sendMsg, setSendMsg] = useState('');
  const [copiedPublicUrl, setCopiedPublicUrl] = useState('');
  const [viewedPopupText, setViewedPopupText] = useState('');

  const styles = {
    wrap: { marginTop: '20px' },
    listSurface: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#6B8E23',
      boxSizing: 'border-box'
    },

    card: {
      border: '6px solid black',
      padding: '18px',
      backgroundColor: 'white',
      marginBottom: '14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    },

    left: { minWidth: '260px' },
    topLine: { fontWeight: 900, fontSize: '20px' },
    subLine: { fontWeight: 900, fontSize: '16px', marginTop: '4px' },

    badge: (status) => {
      const s = String(status || 'draft').toLowerCase();
      let bg = '#cfcfcf';
      if (s === 'sent') bg = '#4da3ff';
      if (s === 'approved') bg = '#39FF14';
      if (s === 'declined') bg = '#ff4444';
      if (s === 'converted') bg = '#888888';
      return {
        backgroundColor: bg,
        color: 'black',
        padding: '8px 14px',
        fontWeight: 900,
        border: '3px solid black',
        textTransform: 'uppercase',
        fontSize: '14px'
      };
    },

    manageBtn: {
      backgroundColor: 'black',
      color: '#FFA500',
      padding: '12px 24px',
      border: '4px solid #E8761D',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },

    viewedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: '#ff1f1f',
      color: 'white',
      padding: '10px 14px',
      border: '3px solid black',
      borderRadius: '8px',
      fontWeight: 900,
      fontSize: '14px',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      flexShrink: 0
    },

    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.9)',
      zIndex: 2000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '14px'
    },

    modal: { backgroundColor: 'white', border: '10px solid black', padding: '24px', width: '100%', maxWidth: '520px' },

    sharePopup: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 2100,
      width: 'calc(100% - 32px)',
      maxWidth: '440px',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      border: '8px solid black',
      boxShadow: '0 0 0 6px #E8761D',
      padding: '16px'
    },

    shareDimmer: {
      position: 'fixed',
      inset: 0,
      zIndex: 2050,
      backgroundColor: 'rgba(0,0,0,0.65)'
    },

    viewedPopupOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 2200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      backgroundColor: 'rgba(0,0,0,0.65)'
    },

    viewedPopup: {
      width: '100%',
      maxWidth: '380px',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      border: '6px solid black',
      boxShadow: '0 0 0 5px #ff1f1f',
      padding: '20px',
      textAlign: 'center'
    },

    btn: {
      width: '100%',
      padding: '15px',
      marginBottom: '10px',
      fontWeight: 900,
      border: '4px solid black',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    }
  };

  useEffect(() => {
    if (!activeMenu) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [activeMenu]);

  const visibleEstimates = useMemo(() => {
    return estimates || [];
  }, [estimates]);

  const hoverBlue = (base, key) => {
    if (hoverKey !== key) return base;
    return { ...base, backgroundColor: '#FFC107', color: 'black', borderColor: 'black' };
  };

  const handleCopyLink = async () => {
    if (!activeMenu) return;
    setSendMsg('');
    setCopiedPublicUrl('');
    setSendingLink(true);
    try {
      const API = getApiBase();
      const id = activeMenu.external_id || activeMenu.id;
      const res = await fetch(`${API}/api/estimates/${encodeURIComponent(String(id))}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() }
      });
      const data = await res.json();
      if (!res.ok) {
        setCopiedPublicUrl('');
        setSendMsg(`❌ ${data.error || 'Failed to get link.'}`);
        return;
      }
      await navigator.clipboard.writeText(data.url);
      setSendMsg('✅ Estimate link copied to clipboard!');
      setCopiedPublicUrl(data.url);
    } catch (e) {
      setCopiedPublicUrl('');
      setSendMsg('❌ Failed to copy link — please try again.');
    } finally {
      setSendingLink(false);
    }
  };

  const handleTextCustomer = () => {
    if (!activeMenu || !copiedPublicUrl) return;
    const rawPhone = String(
      activeMenu.mobile_phone ||
      activeMenu.customer_phone ||
      activeMenu.office_phone ||
      ''
    ).trim();
    if (!rawPhone) {
      setSendMsg('NO CUSTOMER PHONE AVAILABLE.');
      return;
    }
    const normalizedPhone = rawPhone.startsWith('+')
      ? `+${rawPhone.slice(1).replace(/\D/g, '')}`
      : rawPhone.replace(/\D/g, '');
    if (!normalizedPhone) {
      setSendMsg('NO CUSTOMER PHONE AVAILABLE.');
      return;
    }
    const body = `Here is your estimate: ${copiedPublicUrl}`;
    window.location.href = `sms:${normalizedPhone}?body=${encodeURIComponent(body)}`;
  };

  const handleEmailCustomer = () => {
    if (!activeMenu || !copiedPublicUrl) return;
    const email = String(activeMenu.email || '').trim();
    if (!email) {
      setSendMsg('NO CUSTOMER EMAIL AVAILABLE.');
      return;
    }
    const estimateNumber = String(activeMenu.estimate_number || '').trim();
    const customerName = String(activeMenu.customer_name || '').trim();
    const subject = `Estimate ${estimateNumber}`.trim();
    const greeting = customerName ? `Hello ${customerName},` : 'Hello,';
    const body = `${greeting}\r\n\r\nHere is your estimate:\r\n\r\n${copiedPublicUrl}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div style={styles.wrap}>
      <style>{`
        .jf-share-btn { transition: filter 120ms ease, transform 120ms ease; }
        .jf-share-btn:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          filter: brightness(1.02);
        }
        .jf-share-btn:active { transform: translateY(1px); }
        .jf-est-manage-action {
          transition:
            background-color 120ms ease,
            color 120ms ease,
            border-color 120ms ease,
            filter 120ms ease,
            transform 120ms ease;
        }
        .jf-est-manage-action:hover { filter: brightness(1.05); }
        .jf-est-manage-action:active { transform: translateY(1px); }
        .jf-est-manage-approved:hover {
          background: #86FF70 !important;
          color: #000 !important;
          border-color: #166534 !important;
        }
        .jf-est-manage-convert:hover {
          background: #FCD34D !important;
          color: #000 !important;
          border-color: #92400E !important;
        }
        .jf-est-manage-copy:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
        }
        .jf-est-manage-declined:hover {
          background: #F87171 !important;
          color: #000 !important;
          border-color: #7F1D1D !important;
        }
        .jf-est-manage-delete:hover {
          background: #E7B888 !important;
          color: #000 !important;
          border-color: #000 !important;
        }
        .jf-est-manage-draft:hover {
          background: #E5E7EB !important;
          color: #000 !important;
          border-color: #4B5563 !important;
        }
        .jf-est-manage-duplicate:hover {
          background: #9B6DFF !important;
          color: #000 !important;
          border-color: #3B1D6B !important;
        }
        .jf-est-manage-edit:hover {
          background: #E8F3EC !important;
          color: #14532D !important;
          border-color: #166534 !important;
        }
        .jf-est-manage-sent:hover {
          background: #93C5FD !important;
          color: #000 !important;
          border-color: #1D4ED8 !important;
        }
        .jf-est-manage-view:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #E8761D !important;
        }
        .jf-est-manage-close:hover {
          background: #374151 !important;
          color: #fff !important;
          border-color: #000 !important;
        }
      `}</style>
      {listControls}

      <div style={styles.listSurface}>
        {visibleEstimates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', border: '4px dashed black', fontWeight: 900 }}>
            {isGlobalSearchActive ? 'NO ESTIMATES MATCH YOUR SEARCH' : 'NO ESTIMATES FOUND'}
          </div>
        ) : (
          visibleEstimates.map(est => (
            <div key={est.id} style={styles.card}>
            <div style={styles.left}>
              <div style={styles.topLine}>
                {est.estimate_number || est.invoice_number || 'EST'} — {est.company || est.customer_name || 'No Name'}
              </div>
              <div style={{ ...styles.subLine, fontSize: 'clamp(14px, 5vw, 20px)', whiteSpace: 'nowrap' }}>TOTAL: {money(est.total)}</div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {est.first_viewed_at && (
                <button
                  type="button"
                  title={viewedTitle(est.first_viewed_at)}
                  style={styles.viewedBadge}
                  onClick={() => setViewedPopupText(viewedTitle(est.first_viewed_at))}
                >
                  <Eye size={17} strokeWidth={3} aria-hidden="true" />
                  VIEWED
                </button>
              )}
              <div style={styles.badge(est.status)}>{est.status || 'draft'}</div>
              <button
                style={hoverBlue(styles.manageBtn, `manage_${est.id}`)}
                onMouseEnter={() => setHoverKey(`manage_${est.id}`)}
                onMouseLeave={() => setHoverKey('')}
                onClick={() => { setActiveMenu(est); setSendMsg(''); setCopiedPublicUrl(''); }}
              >
                MANAGE
              </button>
            </div>
            </div>
          ))
        )}
      </div>

      {viewedPopupText && (
        <div style={styles.viewedPopupOverlay}>
          <div style={styles.viewedPopup}>
            <div style={{ fontWeight: 900, fontSize: '22px', marginBottom: '12px' }}>
              ESTIMATE VIEWED
            </div>
            <div style={{ fontWeight: 800, lineHeight: 1.45, marginBottom: '18px' }}>
              {viewedPopupText}
            </div>
            <button
              className="jf-share-btn"
              style={{ ...styles.btn, backgroundColor: '#E8761D', marginBottom: 0 }}
              onClick={() => setViewedPopupText('')}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* MANAGE MODAL */}
      {activeMenu && (
        <div style={styles.overlay} onClick={() => { setActiveMenu(null); setSendMsg(''); setCopiedPublicUrl(''); }}>
          <div
            style={{
              ...styles.modal,
              backgroundColor: '#fff7ed',
              boxSizing: 'border-box',
              maxHeight: 'calc(100dvh - 24px)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ fontWeight: 900, marginTop: 0 }}>
              ESTIMATE #{activeMenu.estimate_number || activeMenu.invoice_number}
            </h2>

            <button
              className="jf-est-manage-action jf-est-manage-approved"
              style={{ ...styles.btn, backgroundColor: '#39FF14', color: 'black', border: '4px solid #166534' }}
              onClick={() => { if (onSetStatus) onSetStatus(activeMenu, 'approved'); }}
            >
              APPROVED
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-convert"
              style={{ ...styles.btn, backgroundColor: '#F59E0B', color: 'black', border: '4px solid #92400E' }}
              onClick={() => {
                if (typeof onConvertToInvoice === 'function') onConvertToInvoice(activeMenu);
                else alert('Convert-to-invoice handler is not wired yet.');
                setActiveMenu(null);
                setCopiedPublicUrl('');
              }}
            >
              🔄 CONVERT TO INVOICE
            </button>

            {/* COPY ESTIMATE LINK — replaces EMAIL + TEXT buttons */}
            <button
              className="jf-est-manage-action jf-est-manage-copy"
              style={{
                ...styles.btn,
                backgroundColor: 'black',
                color: '#FFA500',
                border: '4px solid #E8761D',
                opacity: sendingLink ? 0.6 : 1,
                cursor: sendingLink ? 'not-allowed' : 'pointer'
              }}
              onClick={handleCopyLink}
              disabled={sendingLink}
            >
              {sendingLink ? '⏳ GETTING LINK...' : '🔗 COPY ESTIMATE LINK'}
            </button>

            {sendMsg && (
              <div style={{
                padding: '12px',
                marginBottom: '10px',
                border: '3px solid black',
                fontWeight: 900,
                fontSize: '14px',
                backgroundColor: sendMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2'
              }}>
                {sendMsg}
              </div>
            )}

            {copiedPublicUrl && (
              <>
                <div style={styles.shareDimmer} onClick={e => e.stopPropagation()} />
                <div style={styles.sharePopup}>
                  <h3 style={{ margin: '0 0 8px', textAlign: 'center', fontWeight: 900, fontSize: '22px' }}>
                    LINK READY TO SHARE!
                  </h3>
                  {sendMsg.startsWith('NO CUSTOMER') && (
                    <div style={{ padding: '10px', marginBottom: '10px', border: '3px solid black', backgroundColor: '#fee2e2', fontWeight: 900, textAlign: 'center' }}>
                      {sendMsg}
                    </div>
                  )}
                  <button
                    className="jf-share-btn"
                    style={{ ...styles.btn, backgroundColor: '#39FF14', marginBottom: '8px' }}
                    onClick={handleTextCustomer}
                  >
                    TEXT NOW
                  </button>
                  <button
                    className="jf-share-btn"
                    style={{ ...styles.btn, backgroundColor: '#E8761D', marginBottom: '8px' }}
                    onClick={handleEmailCustomer}
                  >
                    EMAIL NOW
                  </button>
                  <button
                    className="jf-share-btn"
                    style={{ ...styles.btn, backgroundColor: 'black', color: 'white', marginBottom: 0 }}
                    onClick={() => setCopiedPublicUrl('')}
                  >
                    CLOSE NOW
                  </button>
                  <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 800, lineHeight: 1.45, textAlign: 'center' }}>
                    <div>
                      Texting requires a mobile device<br />
                      or a configured SMS app.
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      Your link is still on your clipboard<br />
                      and ready to paste anywhere.
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      Paste it into any email or messaging app.
                    </div>
                  </div>
                </div>
              </>
            )}

            <button
              className="jf-est-manage-action jf-est-manage-declined"
              style={{ ...styles.btn, backgroundColor: '#DC2626', color: 'white', border: '4px solid #7F1D1D' }}
              onClick={() => { if (onSetStatus) onSetStatus(activeMenu, 'declined'); }}
            >
              DECLINED
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-delete"
              style={{ ...styles.btn, backgroundColor: '#754C38', color: '#fff7ed', border: '4px solid black' }}
              onClick={() => {
                if (window.confirm('DELETE PERMANENTLY?')) {
                  if (typeof onDelete === 'function') onDelete(activeMenu.id);
                }
                setActiveMenu(null);
                setCopiedPublicUrl('');
              }}
            >
              🗑️ DELETE
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-draft"
              style={{ ...styles.btn, backgroundColor: '#D1D5DB', color: 'black', border: '4px solid #4B5563' }}
              onClick={() => { if (onSetStatus) onSetStatus(activeMenu, 'draft'); }}
            >
              DRAFT
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-duplicate"
              style={{ ...styles.btn, backgroundColor: '#6F42C1', color: 'white', border: '4px solid #3B1D6B' }}
              onClick={() => {
                if (typeof onDuplicate !== 'function') return;
                onDuplicate(activeMenu);
                setActiveMenu(null);
                setSendMsg('');
                setCopiedPublicUrl('');
              }}
            >
              DUPLICATE NOW
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-edit"
              style={{ ...styles.btn, backgroundColor: '#D9F2D0', color: '#14532D', border: '4px solid #166534' }}
              onClick={() => {
                if (typeof onEdit === 'function') onEdit(activeMenu);
                setActiveMenu(null);
                setCopiedPublicUrl('');
              }}
            >
              ✏️ EDIT
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-sent"
              style={{ ...styles.btn, backgroundColor: '#4DA3FF', color: 'black', border: '4px solid #1D4ED8' }}
              onClick={() => { if (onSetStatus) onSetStatus(activeMenu, 'sent'); }}
            >
              SENT
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-view"
              style={{ ...styles.btn, backgroundColor: '#E8761D', color: 'black', border: '4px solid #9A3412' }}
              onClick={() => {
                if (typeof onViewPrint === 'function') onViewPrint(activeMenu);
                setActiveMenu(null);
                setCopiedPublicUrl('');
              }}
            >
              📄 VIEW / PRINT
            </button>

            <button
              className="jf-est-manage-action jf-est-manage-close"
              style={{ ...styles.btn, backgroundColor: 'black', color: 'white', border: '4px solid black' }}
              onClick={() => { setActiveMenu(null); setSendMsg(''); setCopiedPublicUrl(''); }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateList;
